import {IGroupAction} from "./group-actions.service";
import {FnUiDialogService} from "../../shared/fn-ui-dialog/services/fn-ui-dialog.service";
import {DataService, NotificationService} from "@fgpp-ui/components";
import {
  FxDealCancellationModalComponent
} from "../../message/messageTypes/fx-provider/fx-deal-cancellation-modal/fx-deal-cancellation-modal.component";
import {inject, Injectable} from "@angular/core";
import {Observable, EMPTY, catchError, map, throwError} from "rxjs";
import {PROFILE_ID} from "../../profiles/components/profiles-grid/profiles-grid.component";
import {ModalsService} from "../../shared/fn-ui-modals/services/modals.service";
import {switchMap} from "rxjs/operators";
import {TranslateService} from "@ngx-translate/core";
import {ProfilesGroupActionsStrategyInterface} from "./profiles-group-actions-strategy.interface";
import {HttpClient} from "@angular/common/http";

@Injectable()
export class FxDealsCancellationService implements ProfilesGroupActionsStrategyInterface {

  protected dataService: DataService = inject(DataService);
  protected httpClient: HttpClient = inject(HttpClient); // need to use this for some calls as dataService.get() is not working
  protected dialogService: FnUiDialogService = inject(FnUiDialogService);
  protected notificationService: NotificationService = inject(NotificationService);
  protected translate: TranslateService = inject(TranslateService);
  protected modalService: ModalsService = inject(ModalsService);
  private officeField = 'EXTERNAL_FX-OFFICE';

  private getSelectedOffice(selectedRows: any[], officeFieldName: string) {
    if (selectedRows.length > 0) {
      const selectOffices = selectedRows.map(row => row[officeFieldName]);
      // find distinct offices in selectOffices
      const offices = new Set(selectOffices);
      if (offices && offices.size > 1) {
        return null;
      } else {
        // get 0th value from set
        return Array.from(offices)[0];
      }
    }
    return null;
  }

  public getProfileGroupActions(profileId: string, selectedRows: any[]): Observable<any> {
    if (selectedRows.length > 1) {
      return new Observable(observer => {
        observer.error('Multiple record selection not allowed');
      });
    }
    let office = '';
    const url = 'do/PROFILE/' + profileId + '/groupActions';
    if (profileId === PROFILE_ID.CANCEL_FX_DEAL_PROFILE_ID) {
      office = this.getSelectedOffice(selectedRows, this.officeField);
      if (!office) {
        // return error in observable
        return new Observable(observer => {
          observer.error('profiles.group_action.cancel.error_multiple_offices');
        });
      }
      return this.dataService.getFromCache(`${url}?office=${office}`);
    }
  }

  private handleOffsetResponse(response: any, uid: string): Observable<any> {
    const config = {
      offset: response.offsetAmt,
      currency: response.offsetCcy,
      expireInterval: response.expTimeInMillis,
      uid: uid
    };
    const dialogRef = this.modalService.open(FxDealCancellationModalComponent, {data: config, width: '500px'});
    return dialogRef.afterClosed();
  }

  public executeProfileGroupActions(profileId: string, action: IGroupAction, recordsUniqueIds: Array<string>): Observable<any> {
    if (!recordsUniqueIds || recordsUniqueIds.length === 0) {
      return EMPTY; // No action
    }
    const uid = recordsUniqueIds[0];
    return this.httpClient.get(`do/fxRateController/cancelDealAuth?uid=${uid}`).pipe(
      catchError((errorResponse) => {
        let errorMessage = 'message.unexpected_error_code_28560';
        if (errorResponse.error.errorCode && errorResponse.error.errorCode === 27301) {
          errorMessage = 'fx_deal_cancellation.payment_not_cancelled';
        } else if (errorResponse.error.errorCode && errorResponse.error.errorCode === 27302) {
          errorMessage = 'fx_deal_cancellation.deal_in_cancelled_status';
        } else if (errorResponse.error.errorCode && errorResponse.error.errorCode === 27305) {
          errorMessage = 'fx_deal_cancellation.invalid_request';
        } else if (errorResponse.error.errorCode && errorResponse.error.errorCode === 13169) {
          errorMessage = 'fx_deal_cancellation.interface_not_active';
        } else if (errorResponse.error.errorCode && errorResponse.error.errorCode === 9034) {
          errorMessage = 'fx_deal_cancellation.interface_error';
        }
        return throwError(() => new Error(errorMessage));
      }),
      switchMap((response: any) => {
        if (response.returnCode === 0 && response.cancelFxResponse && response.cancelFxResponse.offsetAmt) {
          return this.handleOffsetResponse(response.cancelFxResponse, uid);
        }
      })
    );
  }
}
